import React from "react";
import { RouterProvider } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { SWRConfig } from "swr";
import { initializeApp } from "firebase/app";
import posthog, { type PostHogConfig } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import * as Sentry from "@sentry/react";

import router from "router";
import config from "config";
import { FacebookPixelProvider } from "components/FacebookPixelProvider";
import ErrorDisplay from "components/ErrorDisplay";
import { readOrMintPersonSignalId } from "analytics/personSignalId";
import { AnalyticsProvider } from "analytics/AnalyticsContext";
import { maskCapturedNetworkRequest } from "analytics/replay-mask";
import { QUOTE_GROUP_TYPE, quoteIdFromPath } from "pages/QuoteFlow/quote-group";

import { FirebaseProvider } from "./firebase";
import { UserProvider } from "./context/UserContext";
import { SignalProvider } from "./context/SignalContext";
import "./index.css";

function ErrorFallback({ error }: { error: unknown }) {
  const message =
    error instanceof Error ? error.message : "An unknown error occurred.";
  return <ErrorDisplay message={message} />;
}

const app = initializeApp(config.firebaseConfig);

const FACEBOOK_PIXEL = "997529130982379";
const swrConfig = {
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};
// Read once at module load; null only when the browser offers no cookie
// or random source, in which case PostHog keeps its own anonymous id.
const personSignalId = readOrMintPersonSignalId();

const posthogConfig: Partial<PostHogConfig> = {
  api_host: "https://ph.after.com",
  ui_host: "https://us.posthog.com",
  // The same anonymous device id abra tracks signals under (`a_psid`),
  // shared here so PostHog events and signal events join on one key from
  // the first pageview. NEVER identify on this id — identify stays keyed
  // on personId (see UserContext).
  ...(personSignalId ? { bootstrap: { distinctID: personSignalId } } : {}),
  // The quote flow keys its flags to the quote. Grouping here runs before
  // the client's first flags request, so that request is already
  // quote-keyed: one round trip instead of two, and no flag is ever
  // evaluated ungrouped on a quote page. The quote flow owns the group
  // from here on — its flags hook re-groups and describes it, and the
  // page resets it on leave.
  loaded: (client) => {
    const quoteId = quoteIdFromPath(window.location.pathname);
    if (quoteId) client.group(QUOTE_GROUP_TYPE, quoteId);
  },
  // The quote flow's handoff credential rides the URL (a link token, or a
  // signed lead payload). PostHog masks those params at the source — the
  // current URL, the referrer, the first-seen URL it persists — so no
  // event carries them. Signals does the same in SignalContext.
  mask_personal_data_properties: true,
  custom_personal_data_properties: ["t", "ped", "sig", "exp"],
  session_recording: {
    maskCapturedNetworkRequestFn: maskCapturedNetworkRequest,
  },
};

// Dev-only handle for QA, e.g. overriding feature flags from the console.
if (__DEV__) {
  (window as any).posthog = posthog;
}

export default function App() {
  return (
    <React.StrictMode>
      <HelmetProvider>
        <Helmet defaultTitle="After.com" titleTemplate="%s | After.com" />

        <PostHogProvider apiKey={config.posthogKey} options={posthogConfig}>
          <FirebaseProvider app={app}>
            <UserProvider>
              <SWRConfig value={swrConfig}>
                <SignalProvider>
                  <AnalyticsProvider>
                    <FacebookPixelProvider pixelId={FACEBOOK_PIXEL}>
                      <Sentry.ErrorBoundary
                        fallback={({ error }) => (
                          <ErrorFallback error={error} />
                        )}
                      >
                        <RouterProvider router={router} />
                      </Sentry.ErrorBoundary>
                    </FacebookPixelProvider>
                  </AnalyticsProvider>
                </SignalProvider>
              </SWRConfig>
            </UserProvider>
          </FirebaseProvider>
        </PostHogProvider>
      </HelmetProvider>
    </React.StrictMode>
  );
}
